import { useIntl } from 'react-intl';
import { Typography, useTheme } from '@rossum/ui/material';
import { BarChart } from '@rossum/ui/x-charts-pro';
import { formatNumberToLocale } from '../../../../../../lib/helpers';
import { CommonXAxisProps } from '../../types';
import { ChartWrapper } from './ChartWrapper';
import { CustomLegend } from './CustomLegend';
import { CustomTooltip } from './CustomTooltip';
import { getChartColors } from './styles';
import { CHART_HEIGHT, getCommonYAxisProps } from './utils';

type AutoationCountsChartProps = {
  automationRateChartDataset: {
    automatedCount: number[];
    nonAutomatedCount: number[];
    automationRate: number[];
    date: string[];
  };
  totals: {
    automationRate: number;
    totalAutomated: number;
    totalDocs: number;
  };
  xAxisProps: CommonXAxisProps;
};

export const AutomationCountsChart = ({
  automationRateChartDataset,
  totals,
  xAxisProps,
}: AutoationCountsChartProps) => {
  const theme = useTheme();
  const intl = useIntl();

  const { primary, secondary } = getChartColors(theme);

  const automatedLabel = intl.formatMessage({
    id: 'containers.settings.queues.queue.automation.charts.automationCounts.series.automated',
  });

  const nonAutomatedLabel = intl.formatMessage({
    id: 'containers.settings.queues.queue.automation.charts.automationCounts.series.manual',
  });
  return (
    <ChartWrapper elevation={0}>
      <Typography variant="h5" sx={{ span: { color: 'text.secondary' } }}>
        {formatNumberToLocale(totals.totalAutomated)}{' '}
        <span>
          {intl.formatMessage(
            {
              id: 'containers.settings.queues.queue.automation.charts.automationCounts.title',
            },
            { total: formatNumberToLocale(totals.totalDocs) }
          )}
        </span>
      </Typography>

      <BarChart
        height={CHART_HEIGHT}
        series={[
          {
            data: automationRateChartDataset.automatedCount,
            type: 'bar',
            stack: 'automationCounts',
            color: primary,
            label: automatedLabel,
            labelMarkType: 'circle',
            valueFormatter: val => (val ? formatNumberToLocale(val) : null),
          },
          {
            data: automationRateChartDataset.nonAutomatedCount,
            type: 'bar',
            stack: 'automationCounts',
            color: secondary,
            label: nonAutomatedLabel,
            labelMarkType: 'circle',
            valueFormatter: val => (val ? formatNumberToLocale(val) : null),
          },
        ]}
        yAxis={[
          {
            ...getCommonYAxisProps(theme.palette.text.secondary),
            position: 'right',
            valueFormatter: (
              val: (typeof automationRateChartDataset.automatedCount)[number]
            ) => formatNumberToLocale(val),
          },
        ]}
        xAxis={[
          {
            data: automationRateChartDataset.date,
            tickLabelStyle: { fill: theme.palette.text.secondary },
            disableLine: true,
            disableTicks: true,
            ...xAxisProps,
          },
        ]}
        grid={{ horizontal: true }}
        borderRadius={4}
        margin={{ left: 0, right: 0 }}
        slotProps={{
          legend: {
            position: { vertical: 'bottom', horizontal: 'start' },
          },
        }}
        slots={{
          legend: () => (
            <CustomLegend
              primarySeries={automatedLabel}
              secondarySeries={nonAutomatedLabel}
            />
          ),
          tooltip: CustomTooltip,
        }}
      />
    </ChartWrapper>
  );
};
